import React, { useEffect, useState } from "react";
import store from "../../redux/store";
import {
  Box,
  Card,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  SimpleGrid,
  Spacer,
  Text,
} from "@chakra-ui/react";
import { FiChevronLeft, FiChevronRight, FiSearch } from "react-icons/fi";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import DisplayData from "../../components/DisplayData";
import { get_customer_list } from "../../redux/customerSlice";
import GasCylinderLoader from "../../components/GasCylinderLoader";

const CustomerList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState(10);
  const [page, setpage] = useState(1);
  const [initialValue, setInitialValue] = useState(1);
  const [searchTerm, setSearchTerm] = useState(""); // ✅ Search term

  const { customer_list, total_customer_count,loading } = useSelector(
    (state) => state.customers
  );

  useEffect(() => {
    const savedPage = location.state?.page || 1;
    const savedRows = location.state?.rows || 5;
    const savedInitialValue = location.state?.initialValue || 1;

    setpage(savedPage);
    setRows(savedRows);
    setInitialValue(savedInitialValue);

    store.dispatch(get_customer_list({ page: savedPage, rows: savedRows }));
  }, [location.state]);

  // ✅ Filter customers based on name (case-insensitive)
  const filteredCustomers = customer_list.filter((el) =>
    el.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const onPrevious = () => {
    if (page > 1) {
      setInitialValue(Number(initialValue) - Number(rows));
      setpage(page - 1);
      store.dispatch(get_customer_list({ page: page - 1, rows }));
    }
  };

  const onNext = () => {
    if (rows * page < total_customer_count) {
      setInitialValue(Number(initialValue) + Number(rows));
      setpage(page + 1);
      store.dispatch(get_customer_list({ page: page + 1, rows }));
    }
  };

  return (
    <>
      <Card
        bg="#fff"
        paddingInline={5}
        paddingBlock={2}
        display="flex"
        flexDirection={["column", "column", "row"]}
        alignItems={["stretch", "stretch", "center"]}
        gap={3}
      >
        <Heading fontSize="xl">Customer List</Heading>

        <Spacer display={["none", "none", "block"]} />

        {/* ✅ Search Input */}
        <InputGroup
          bg="#f4f5f7"
          maxW="sm"
          borderRadius={5}
          w={["100%", "100%", "auto"]}
        >
          <Input
            placeholder="Search by customer name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)} // ✅ update search term
          />
          <InputRightElement>
            <FiSearch />
          </InputRightElement>
        </InputGroup>
      </Card>

      {loading ? (<GasCylinderLoader 
      size="sm"
    loadingText="Loading Customers"
  />):(<>
       <SimpleGrid minChildWidth="xs" spacing="10px" mt={4}>
        {filteredCustomers.length > 0 ? (
          filteredCustomers.map((el, i) => (
            <Card
              key={el.id}
              paddingInline={5}
              paddingBlock={2}
              cursor="pointer"
              height="fit-content"
              display="flex"
              flexDirection="column"
              gap={1}
              onClick={() =>
                navigate("/customer-list/customer-detail", {
                  state: {
                    customerId: el.id,
                    branches: el.children,
                    page,
                    rows,
                    initialValue,
                  },
                })
              }
              _hover={{ boxShadow: "md", transform: "translateY(-3px)" }}
              transition="all 0.2s ease"
            >
              {/* Header */}
              <DisplayData
                fs="16px"
                v_color="#007bc9"
                fw="bold"
                value={el.userid}
              />

              {/* Key–Value Pairs */}
              <Box mt={1}>
                {[
                  { title: "", value: "#" + el.id }, // Highlighted ID
                  { title: "Name", value: el.name },
                  { title: "Address", value: el.address },
                  { title: "Mobile No.", value: el.mobile },
                  { title: "Email", value: el.email },
                  { title: "Secondary Email", value: el.email2 },
                  { title: "GST No.", value: el.gst },
                  { title: "PAN No.", value: el.pan },
                  { title: "Pincode", value: el.pincode },
                ].map((item, idx) => (
                  <Flex
                    key={idx}
                    justify="space-between"
                    align="center"
                    mb="1"
                    wrap="nowrap"
                  >
                    <Text fontSize="sm"  color="gray.600" flex="1" noOfLines={1}>
                      {item.title}
                    </Text>
                    <Text
                      fontSize="sm"
                      fontWeight={item.title === "" ? "bold":""}
                      color={item.title === "" ? "blue.500" : "gray.800"} // Highlighted ID color
                      flex="1"
                      textAlign="right"
                      noOfLines={1}
                    >
                      {item.value || "NA"}
                    </Text>
                  </Flex>
                ))}
              </Box>
            </Card>
          ))
        ) : (
          <Card p={5} textAlign="center">
            <Text color="gray.500">No customers found</Text>
          </Card>
        )}
      </SimpleGrid>

      {/* Pagination */}
      <Card alignItems="center" alignSelf="end" gap={3} flexDirection="row">
        <Flex
          w="full"
          h="auto"
          justify={{ base: "center", md: "flex-end" }}
          alignItems="center"
          flexWrap="wrap"
          px={{ base: 4, md: 10 }}
          py={4}
          gap={4}
        >
          <Text fontSize="sm">Rows per page:</Text>
          <Select
            size="sm"
            maxW="70px"
            value={rows}
            onChange={(e) => {
              store.dispatch(
                get_customer_list({ page: page, rows: e.target.value })
              );
              setRows(e.target.value);
            }}
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </Select>

          <Flex gap={1} align="center" fontSize="sm">
            <Text>{initialValue}</Text>
            <Text>-</Text>
            <Text>{rows * page >= total_customer_count ? total_customer_count : rows * page}</Text>
            <Text>of {total_customer_count}</Text>
          </Flex>

          <FiChevronLeft
            cursor="pointer"
            color={page === 1 ? "gray" : "#007bc9"}
            size={25}
            onClick={onPrevious}
          />

          <FiChevronRight
            cursor="pointer"
            color={rows * page >= total_customer_count ? "gray" : "#007bc9"}
            size={25}
            onClick={onNext}
          />
        </Flex>
      </Card>
      </>)}

      {/* ✅ Filtered Customer Cards */}

    </>
  );
};

export default CustomerList;
